import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';
import { NavLink } from 'react-router-dom';
import styles from './styles.module.scss';

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';
import TrainsService from '../../../../../services/data/trains';

// props
import { ITrainResourceShort } from '../../../../../props/data/trains';

// components
import { ActionButton, CommandBar, IColumn, ICommandBarItemProps, IContextualMenuItem, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import Text from './../../../../typography/text';
import Tag from '../../../../uiframeworks/tag';
import TrainForm from '../form';
import PermissionsService from '../../../../../services/permissions';

interface ITrainsListProps {
}

const TrainsList: React.FC<ITrainsListProps> = (props: ITrainsListProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [showingMore, setShowingMore] = React.useState<boolean>(false);
    const [trains, setTrains] = React.useState<ITrainResourceShort[]>([]);
    const [activeTrain, setActiveTrain] = React.useState<ITrainResourceShort | undefined>();
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const hasPermission = PermissionsService.hasPermission(['trains.read.all'], user.permissions);

    const columns: IColumn[] = [
        {
            key: "name",
            name: "Name",
            fieldName: "name",
            minWidth: 150,
            maxWidth: 150,
            onRender: (item: ITrainResourceShort) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <NavLink to={`/operationals/trains/${item.id}`}><Text>{item.name}</Text></NavLink>
                </Stack>
            }
        },
        {
            key: "actions",
            name: "",
            minWidth: 50,
            maxWidth: 50,
            onRender: (item: ITrainResourceShort) => {
                let menuItems: IContextualMenuItem[] = []

                if (PermissionsService.hasPermission(['trains.update.all'], user.permissions)) {
                    menuItems.push({
                        key: "update", text: "Update details", iconProps: { iconName: "Edit" }, onClick: () => {
                            setActiveTrain(item);
                            setActiveSurface('update');
                        }
                    });
                }

                return <Stack.Item styles={{ root: { padding: '2px 0px' } }}>
                    {menuItems.length > 0 ? <Stack.Item className={"detailsListActionRow"}>
                        <ActionButton className={'detailsListActionButton'} menuProps={{
                            items: menuItems
                        }} />
                    </Stack.Item> : null}
                </Stack.Item>;
            }
        },
        {
            key: 'vendor',
            fieldName: 'vendor',
            name: 'Vendor',
            minWidth: 200,
            onRender: (item: ITrainResourceShort) => {
                return (
                    <Stack horizontal styles={{ root: { padding: '4px 0px' } }}>
                        {item.vendor ? <Tag text={item.vendor.name} /> : null}
                    </Stack>
                );
            },
        },
        {
            key: 'fleetType',
            name: 'Type',
            minWidth: 150,
            maxWidth: 150,
            onRender: (item: ITrainResourceShort) => {
                return (
                    <Stack styles={{ root: { padding: '4px 0px' } }}>
                        <Text>{item.fleetType?.name || "-"}</Text>
                    </Stack>
                );
            },
        }
    ];

    useEffect(() => {
        _onRetrieveTrains();
    }, [keyword]);

    const _onRetrieveTrains = async (pageNumber?: number,) => {
        try {
            if (!pageNumber) {
                setLoaded(false);
            }

            const qs: string[] = [];
            if (pageNumber) { qs.push(`page=${pageNumber}`); }
            if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword}`) }

            const results = await TrainsService.retrieve(qs.join("&"));
            setTrains(results);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_trains_error',
                text: `Failed to retrieve trains list. Error: ` + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onKeywordChanged = (value?: string) => {
        setLoaded(true);
        setKeyword(value || "");
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        items.push({
            key: "search",
            onRender: () => {
                return <SearchBox placeholder={"Search ..."} onSearch={_onKeywordChanged} />
            }
        });

        if (PermissionsService.hasPermission(['trains.create'], user.permissions)) {
            farItems.push({
                key: "register",
                text: "Create train",
                iconProps: { iconName: "Add" },
                onRender: () => {
                    return <PrimaryButton text={"Create train"}
                        iconProps={{ iconName: "Add" }}
                        onClick={() => { setActiveSurface('create') }}
                        styles={{ root: { marginLeft: 10 } }} />;
                }
            });
        }

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        setActiveTrain(undefined);

        if (refresh) { _onRetrieveTrains() }
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }} styles={{ root: { marginTop: 10 } }}>
        {hasPermission ? <>
            <CommandBar
                items={getCommandBarItems().items}
                farItems={getCommandBarItems().farItems}
                styles={{
                    root: {
                        padding: 0,
                        height: 'unset',
                        backgroundColor: 'transparent'
                    }
                }}
                ariaLabel="Use left and right arrow keys to navigate between commands" />
            <Stack className={styles.list}>
                {
                    !loaded || (loaded && trains.length > 0) ? (
                        <>
                            <ShimmeredDetailsList
                                setKey="items"
                                items={trains}
                                columns={columns}
                                selectionMode={SelectionMode.none}
                                enableShimmer={!loaded}
                                onShouldVirtualize={() => false}
                                ariaLabelForShimmer="Content is being fetched"
                                ariaLabelForGrid="Item details" />
                        </>
                    ) : null
                }
                {loaded && trains.length < 1 ? <Text>Train(s) not found</Text> : null}
            </Stack>
            {activeSurface === 'create' ? <TrainForm onDismissed={_onSurfaceDismissed} /> : null}
            {activeSurface === 'update' && activeTrain ? <TrainForm trainId={activeTrain.id} onDismissed={_onSurfaceDismissed} /> : null}
        </> : null}
    </Stack>;
};

export default TrainsList;
